import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/", labelKey: "nav.home", testid: "home" },
    { path: "/services", labelKey: "nav.services", testid: "services" },
    { path: "/about", labelKey: "nav.about", testid: "about" },
    { path: "/portfolio", labelKey: "nav.portfolio", testid: "portfolio" },
    { path: "/limited-edition", labelKey: "nav.limited_edition", testid: "limited-edition" },
    { path: "/contact", labelKey: "nav.contact", testid: "contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-[#FAF9F6]/95 backdrop-blur-md shadow-[0_1px_0_rgba(216,212,204,0.5)]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              data-testid="navbar-logo"
              className="font-heading text-2xl tracking-tight text-[#1A1B1A]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              ETERNA <span className="font-light">Motion</span>
            </Link>

            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.testid}`}
                  className={`elegant-link text-xs uppercase tracking-[0.15em] font-body transition-colors duration-300 ${
                    isActive(link.path)
                      ? "text-[#C4A462]"
                      : "text-[#4A4A4A] hover:text-[#1A1B1A]"
                  }`}
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
                >
                  {t(link.labelKey)}
                </Link>
              ))}
              <div className="pl-2 border-l border-[#D8D4CC]/60">
                <LanguageSwitcher />
              </div>
            </div>

            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden text-[#1A1B1A]"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X strokeWidth={1.25} size={24} /> : <Menu strokeWidth={1.25} size={24} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#FAF9F6] pt-24 px-8"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.testid}`}
                  className="font-heading text-3xl tracking-tight text-[#1A1B1A] hover:text-[#C4A462] transition-colors"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                >
                  {t(link.labelKey)}
                </Link>
              ))}
              <div className="pt-8 border-t border-[#D8D4CC]/40">
                <LanguageSwitcher variant="mobile" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
